import { useId, type ReactNode } from 'react';
import { getTicketCta, type TourEvent } from '@/features/events';
import { trackContact, trackInitiateCheckout, useCampaignUrl } from '@/features/tracking';
import { cx } from '@/lib/cx';
import styles from './TicketCta.module.css';

export type TicketCtaVariant = 'standard' | 'premium';

export interface TicketCtaProps {
  event: TourEvent;
  /** `standard` navy (ciudades) · `premium` oro con doble filete (Cancun). */
  variant?: TicketCtaVariant;
  /* ---------------------------------------------------------------------------
   * FILETE DORADO FINO EN EL CANTO. **Solo gobierna `standard`.**
   *
   * POR QUE ES UNA PROP Y NO UNA DECISION DEL COMPONENTE: los seis posters se
   * generaron POR SEPARADO y en esto NO coinciden. Dos agentes midieron su
   * propio PNG y los dos midieron bien:
   *   · `DEMO_3_Miami.png`  — SIN filete. Perfil de pixel cruzando el canto en
   *     los dos ejes (y=986 y x=350): del crema al navy en UN pixel de
   *     antialiasing (#37556F) y R-B entre -90 y -107 en todo el recorrido. Un
   *     filete de oro daria R-B POSITIVO (+40…+80) y no aparece en ningun eje.
   *   · el poster de Mexico — CON filete fino, sobre relleno #073759 (FORGE-ECHO).
   * Y las dos paginas usan `variant="standard"`, asi que el componente no puede
   * acertar con las dos a la vez: quien sabe es el llamador, que tiene su poster.
   *
   * POR DEFECTO **SI**, y el motivo es que este valor no se elige por gusto sino
   * por cual de los dos errores es peor. El filete estaba en el componente desde
   * que lo escribio FORGE-ALPHA; se quito en globaL el 26-jul-2026 con la medida
   * de UN solo poster, o sea que las demas sedes perdieron un ornamento que
   * nadie habia medido en las suyas. Con el defecto en `true`, esta prop deja de
   * ser una retirada global y pasa a ser lo que debe: una EXCEPCION declarada
   * donde hay medida que la respalde (`/miami` la pasa en falso). Una pagina que
   * no se plantee el asunto se queda con el ornamento del sistema en vez de
   * perderlo en silencio — y el silencio es el fallo que hay que evitar.
   *
   * `premium` NO lo mira: su doble filete de oro es parte de lo que `premium` ES
   * (relleno dorado y rotulo navy, al reves que `standard`), no un adorno
   * opcional. Ver `.premium` y `.premium.unavailable` en el CSS.
   * ------------------------------------------------------------------------ */
  hairline?: boolean;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  /** Ornamento a la izquierda del texto (la corona de Cancun). */
  icon?: ReactNode;
  className?: string;
}

/* =============================================================================
 * EL CTA DE COMPRA · aqui es donde la linea roja del ADR-003 §3 se hace codigo.
 * -----------------------------------------------------------------------------
 * Una pagina NO decide si pinta un enlace o no: se lo dice el dato.
 *
 *   salesState 'on-sale'                -> <a href={ticketUrl}> real y externo
 *   salesState 'coming-soon'/'sold-out' -> <button aria-disabled> + estado visible
 *
 * PROHIBIDO y ademas IMPOSIBLE: un href roto, un '#', o un enlace a la home de
 * la plataforma fingiendo ser la ficha del evento. `getTicketCta` devuelve una
 * union discriminada que, sin venta abierta, NI SIQUIERA TIENE campo `href`.
 *
 * Por que `aria-disabled` y no `disabled`:
 * un `disabled` de verdad saca el boton del orden de tabulacion, y entonces el
 * lector de pantalla NUNCA llega a leer el `aria-describedby` que explica por
 * que no se puede comprar todavia. Con `aria-disabled` el boton sigue siendo
 * alcanzable con teclado, se anuncia como no disponible y SI se lee la razon.
 * Mismo aspecto, misma imposibilidad de pulsar, mejor informacion.
 * ========================================================================== */
export function TicketCta({
  event,
  variant = 'standard',
  hairline = true,
  size = 'lg',
  block = false,
  icon,
  className,
}: TicketCtaProps) {
  const cta = getTicketCta(event);
  const noteId = useId();

  /* EL ENLACE SE DECORA AQUÍ, NO EN EL DATO. `getTicketCta` es puro —lo comen los tests y no puede
   * depender de `sessionStorage`—, así que la campaña se engancha en el borde, que es donde hay
   * navegador.
   *
   * Y SE SUSCRIBE, no se lee una vez: la campaña se captura en un efecto, o sea DESPUÉS de este
   * primer pintado. Leyéndola a secas, quien aterriza directo desde un anuncio se quedaba con el
   * enlace pelado para siempre —medido en producción el 12-ago-2026—, que es precisamente el
   * visitante por el que se ha pagado. `useCampaignUrl` repinta cuando la campaña entra.
   *
   * VA AQUÍ ARRIBA, FUERA DEL `if`: un hook dentro de una rama es un hook condicional, y en cuanto
   * un evento pasara de `on-sale` a `sold-out` React se encontraría con otra lista de hooks. Que
   * hoy no ocurra no lo hace correcto. `cta.href` no existe fuera del caso `link`, así que se le
   * pasa cadena vacía y el resultado se ignora. */
  const hrefConCampana = useCampaignUrl(cta.kind === 'link' ? cta.href : '');

  const classes = cx(
    styles.cta,
    styles[variant],
    // La clase se pone SOLO en `standard`. Asi el DOM no arrastra una clase que
    // no gobierna nada en `premium`, y nadie puede leer un `hairline={false}`
    // sobre `premium` como una promesa que el CSS no cumple.
    variant === 'standard' && hairline && styles.hairline,
    styles[`size-${size}`],
    block && styles.block,
    cta.kind !== 'link' && styles.unavailable,
    className,
  );

  if (cta.kind === 'link') {
    const href = hrefConCampana;
    /* Y aquí se avisa a Meta de que este visitante va a comprar. Es el último
     * evento que esta web puede medir: a partir del clic manda la tienda, que
     * tiene su propio píxel —el MISMO id— y cierra el embudo con el `Purchase`.
     * Si no hay consentimiento, la llamada no hace nada: el píxel ni arrancó. */
    const alPulsar = () => {
      const contenido = { id: event.slug, name: `${event.city} · ${event.title}` };
      /* CADA CTA MANDA SU PROPIO EVENTO. El de `whatsapp` abre un chat, no una
       * tienda: es `Contact`. Ver `trackContact` para por que mezclarlos
       * estropea la optimizacion de los anuncios de las otras cinco. */
      if (event.salesState === 'whatsapp') trackContact(contenido);
      else trackInitiateCheckout(contenido);
    };
    return (
      <a className={classes} href={href} target="_blank" rel="noopener noreferrer" onClick={alPulsar}>
        {icon ? (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className={styles.label}>{cta.label}</span>
        <span className="az-visually-hidden"> (se abre en una pestaña nueva)</span>
      </a>
    );
  }

  // El boton se queda: el usuario ve la accion que existira, y por que no ahora.
  return (
    <span className={cx(styles.group, block && styles.block)}>
      <button type="button" className={classes} aria-disabled="true" aria-describedby={noteId}>
        {icon ? (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className={styles.label}>Reservar mi lugar</span>
      </button>
      <p id={noteId} className={styles.note} role="status">
        <span className={styles.noteLabel}>{cta.label}</span>
        <span className={styles.noteText}>{cta.note}</span>
      </p>
    </span>
  );
}
